/**
 * Team Redux Operations (Thunks)
 * Async operations for team data
 */

import * as actions from './team.actions';
import { getTeamMembersByAgency, getInvitationsByAgency, removeAgencyMembership } from '../../services/requests/users-and-permissions/users/users';
import { sendInvitations, updateInvitation, approveRejectInvitation } from '../../services/requests/users-and-permissions/invitations';
import { showErrorToast, showSuccessToast } from '../../components/toast';
import { userEffectiveAgencyIdSelector } from '../../store/user/user.selectors';
import { normalizeTeamMembers, normalizeInvitations } from '../../data/Team/team-normalizers';

/** Default page size for the team-members endpoint */
const DEFAULT_TEAM_MEMBERS_LIMIT = 10;

/**
 * Fetch team members (users + invites) with server-side pagination, sorting, and filtering.
 *
 * All filtering / sorting is done by the backend.
 * The response contains a mixed list where each item has an `is_prospect` boolean.
 *
 * @param {Object} [params] - Query parameters (merged with stored params in Redux)
 * @param {string}  [params.searchTerm]  - Free-text search across name / email
 * @param {number}  [params.page=1]      - 1-based page number
 * @param {number}  [params.limit]       - Rows per page (falls back to stored / default)
 * @param {string}  [params.sortBy]      - 'name' | 'role' | 'last_active'
 * @param {string}  [params.sortOrder]   - 'asc' | 'desc'
 * @param {string}  [params.roleFilter]  - Exact role value, e.g. 'traffic_viewer'
 * @returns {Function} Redux thunk
 */
export const getTeamMembersOperation = (params = {}) => async (dispatch, getState) => {
    // Merge incoming params over whatever is already stored in Redux
    const storedParams = getState().team?.members?.params || {};
    const merged = { ...storedParams, ...params };

    const {
        searchTerm,
        page = 1,
        limit = DEFAULT_TEAM_MEMBERS_LIMIT,
        sortBy,
        sortOrder,
        roleFilter,
    } = merged;

    // Persist the resolved params so the UI can read them back
    dispatch(actions.setTeamMembersParams({ searchTerm, page, limit, sortBy, sortOrder, roleFilter }));
    dispatch(actions.getTeamMembersRequest());

    // Read the effective agency for the current user
    const agencyId = userEffectiveAgencyIdSelector(getState());

    // Compute zero-based offset from 1-based page number
    const offset = (page - 1) * limit;

    try {
        const response = await getTeamMembersByAgency({
            agencyId: agencyId || undefined,
            searchTerm: searchTerm || undefined,
            offset,
            limit,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
            roleFilter: roleFilter || undefined,
        });

        // axios wraps in response.data → { pagination, data }
        const responseData = response?.data;
        const rawData = responseData?.data || [];
        const pagination = responseData?.pagination || { offset: 0, limit, count: 0 };

        // Normalise raw API objects into consistent model shapes
        const data = normalizeTeamMembers(rawData);

        dispatch(actions.getTeamMembersSuccess({ data, pagination }));
        return { data, pagination };
    } catch (error) {
        dispatch(actions.getTeamMembersError(error));
        dispatch(showErrorToast(
            'Failed to load team members',
            'Something went wrong while loading your team. Please try again.',
        ));
        throw error;
    }
};

/**
 * Reset team members state (on unmount or agency change)
 * @returns {Function} Redux thunk
 */
export const resetTeamMembersOperation = () => (dispatch) => {
    dispatch(actions.resetTeamMembers());
};

/** Default page size for the invitations endpoint */
const DEFAULT_REQUESTS_LIMIT = 10;

/**
 * Fetch pending requests (invitations) with server-side pagination, sorting, and filtering.
 *
 * @param {Object} [params] - Query parameters (merged with stored params in Redux)
 * @param {string}  [params.searchTerm]  - Free-text search across name / email
 * @param {number}  [params.page=1]      - 1-based page number
 * @param {number}  [params.limit]       - Rows per page (falls back to stored / default)
 * @param {string}  [params.sortBy]      - 'name' | 'role' | 'last_active'
 * @param {string}  [params.sortOrder]   - 'asc' | 'desc'
 * @param {string}  [params.roleFilter]  - Exact role value, e.g. 'traffic_viewer'
 * @returns {Function} Redux thunk
 */
export const getTeamRequestsOperation = (params = {}) => async (dispatch, getState) => {
    const storedParams = getState().team?.requests?.params || {};
    const merged = { ...storedParams, ...params };

    const {
        searchTerm,
        page = 1,
        limit = DEFAULT_REQUESTS_LIMIT,
        sortBy,
        sortOrder,
        roleFilter,
    } = merged;

    dispatch(actions.setTeamRequestsParams({ searchTerm, page, limit, sortBy, sortOrder, roleFilter }));
    dispatch(actions.getTeamRequestsRequest());

    const agencyId = userEffectiveAgencyIdSelector(getState());

    // Compute zero-based offset from 1-based page number
    const offset = (page - 1) * limit;

    try {
        const response = await getInvitationsByAgency({
            agencyId: agencyId || undefined,
            searchTerm: searchTerm || undefined,
            offset,
            limit,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
            roleFilter: roleFilter || undefined,
        });

        const responseData = response?.data;
        const rawData = responseData?.data || [];
        const pagination = responseData?.pagination || { offset: 0, limit, count: 0 };

        const data = normalizeInvitations(rawData);

        dispatch(actions.getTeamRequestsSuccess({ data, pagination }));
        return { data, pagination };
    } catch (error) {
        dispatch(actions.getTeamRequestsError(error));
        dispatch(showErrorToast(
            'Failed to load requests',
            'Something went wrong while loading requests. Please try again.',
        ));
        throw error;
    }
};

/**
 * Reset team requests state (on unmount or agency change)
 * @returns {Function} Redux thunk
 */
export const resetTeamRequestsOperation = () => (dispatch) => {
    dispatch(actions.resetTeamRequests());
};

/**
 * Send user invitations and refresh team data on success.
 *
 * The API responds with one of:
 *  - 200 success: { successfulInvitationsProcessCount, failedInvitationsProcessCount, errors }
 *  - 400 validation: { statusCode, error, message, errors: [{ field, messages }] }
 *
 * On success the operation dispatches a success toast and re-fetches the team
 * members list. The raw API response is always returned so the calling UI can
 * react to partial failures or validation errors.
 *
 * @param {Object} payload
 * @param {string[]} payload.recipients
 * @param {string}   payload.assigned_role
 * @param {string}   payload.agency_id
 * @param {string[]} [payload.jurisdictions]
 * @param {string}   [payload.customized_message]
 * @returns {Function} Redux thunk that resolves with the API response
 */
export const sendInvitationsOperation = (payload) => async (dispatch) => {
    try {
        const response = await sendInvitations(payload);

        const successCount = response?.successfulInvitationsProcessCount ?? 0;

        if (successCount > 0) {
            dispatch(showSuccessToast(
                `${successCount} user(s) invited successfully.`,
            ));
            // Refresh team members list to reflect new invitations
            dispatch(getTeamMembersOperation({ page: 1 }));
        }

        return { success: true, data: response };
    } catch (error) {
        // 422 — the backend could not create the invitation(s), typically
        // because the invitee already has an account or a pending invitation.
        // Keep the modal open so the user can correct the email list.
        const isAlreadyExistsError = error?.statusCode === 422;

        if (isAlreadyExistsError) {
            return { success: false, validationError: true, alreadyExists: true, data: error };
        }

        // Check for validation error (400 — email validation, etc.)
        const isValidationError = error?.statusCode === 400 || error?.error === 'Bad Request';

        if (isValidationError) {
            // Return the validation errors so the modal can highlight fields
            return { success: false, validationError: true, data: error };
        }

        // Generic server error
        dispatch(showErrorToast(
            'Failed to send invitations',
            'Something went wrong. Please try again.',
        ));

        return { success: false, validationError: false, data: error };
    }
};

/**
 * Update the role on an existing invitation request.
 *
 * Calls PATCH /invitations/update with query params.
 *
 * On success the operation shows a toast and re-fetches the requests list so
 * the table reflects the updated role.
 *
 * @param {Object} payload
 * @param {string}   payload.agency_id       - Agency ID
 * @param {string}   payload.invitee_email   - Invitee email address
 * @param {string}   payload.created_at_ts   - ISO timestamp of invitation creation
 * @param {string}   payload.assigned_role   - New role value
 * @param {string[]} [payload.jurisdictions] - Jurisdiction IDs (for traffic_viewer)
 * @param {string}   payload.request_type    - e.g. 'update_role'
 * @param {string}   [payload.change_reason] - Admin-provided reason for the change
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const updateInvitationOperation = (payload) => async (dispatch) => {
    try {
        const response = await updateInvitation(payload);

        dispatch(showSuccessToast('Role updated successfully.'));

        // Refresh the requests list to reflect the new status
        dispatch(getTeamRequestsOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        const isValidationError = error?.statusCode === 400 || error?.error === 'Bad Request';

        if (isValidationError) {
            dispatch(showErrorToast(
                'Failed to update role',
                error?.message || 'Please check the role and jurisdictions and try again.',
            ));
            return { success: false, validationError: true, data: error };
        }

        dispatch(showErrorToast(
            'Failed to update role',
            'Something went wrong. Please try again.',
        ));

        return { success: false, validationError: false, data: error };
    }
};

/**
 * Resend an expired invitation from the Team page.
 *
 * Calls PATCH /invitations/update with request_type: 'retry_invite'.
 * On success, shows a toast and refreshes the team members list.
 *
 * @param {Object} payload
 * @param {string}   payload.agency_id       - Agency ID
 * @param {string}   payload.invitee_email   - Invitee email address
 * @param {string}   payload.created_at_ts   - ISO timestamp of invitation creation
 * @param {string}   payload.assigned_role   - Current role on the invitation
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const resendTeamInviteOperation = (payload) => async (dispatch) => {
    try {
        const response = await updateInvitation({
            ...payload,
            request_type: 'retry_invite',
        });

        dispatch(showSuccessToast('Invitation resent successfully.'));
        dispatch(getTeamMembersOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to resend invitation',
            error?.message || 'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};

/**
 * Resend an expired invitation from the Requests page.
 *
 * Calls PATCH /invitations/update with request_type: 'retry_invite'.
 * On success, shows a toast and refreshes the requests list.
 *
 * @param {Object} payload
 * @param {string}   payload.agency_id       - Agency ID
 * @param {string}   payload.invitee_email   - Invitee email address
 * @param {string}   payload.created_at_ts   - ISO timestamp of invitation creation
 * @param {string}   payload.assigned_role   - Current role on the invitation
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const resendRequestInviteOperation = (payload) => async (dispatch) => {
    try {
        const response = await updateInvitation({
            ...payload,
            request_type: 'retry_invite',
        });

        dispatch(showSuccessToast('Invitation resent successfully.'));
        dispatch(getTeamRequestsOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to resend invitation',
            error?.message || 'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};

/**
 * Approve or reject a pending invitation request.
 *
 * Calls PATCH /invitations/approve with query params.
 *
 * On success the operation shows a toast and re-fetches the requests list.
 *
 * @param {Object} payload
 * @param {string}  payload.agency_id       - Agency ID
 * @param {string}  payload.invitee_email   - Invitee email address
 * @param {string}  payload.created_at_ts   - ISO timestamp of invitation creation
 * @param {boolean} payload.is_approved     - true to approve, false to reject
 * @param {string}  [payload.change_reason] - Reason for the decision
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const approveRejectInvitationOperation = (payload) => async (dispatch) => {
    const actionLabel = payload.is_approved ? 'approved' : 'rejected';

    try {
        const response = await approveRejectInvitation(payload);

        dispatch(showSuccessToast(`Request ${actionLabel} successfully.`));

        // Refresh the requests list to reflect the new status
        dispatch(getTeamRequestsOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        const isValidationError = error?.statusCode === 400 || error?.error === 'Bad Request';

        if (isValidationError) {
            dispatch(showErrorToast(
                `Failed to ${payload.is_approved ? 'approve' : 'reject'} request`,
                error?.message || 'Please try again.',
            ));
            return { success: false, validationError: true, data: error };
        }

        dispatch(showErrorToast(
            `Failed to ${payload.is_approved ? 'approve' : 'reject'} request`,
            'Something went wrong. Please try again.',
        ));

        return { success: false, validationError: false, data: error };
    }
};

/**
 * Remove a user's membership from an agency and refresh the team list.
 *
 * Calls POST /users/remove-agency-membership with the user's email, the agency
 * ID, and the membership creation timestamp. On success a toast is shown and
 * the team members list is re-fetched so the removed user disappears.
 *
 * @param {Object} payload
 * @param {string} payload.user_email    - Email of the user to remove
 * @param {string} payload.agency_id     - Agency to remove the user from
 * @param {string} payload.created_at_ts - ISO timestamp of the membership
 * @returns {Function} Redux thunk that resolves with { success, data }
 */
export const removeAgencyMembershipOperation = (payload) => async (dispatch) => {
    try {
        const response = await removeAgencyMembership(payload);

        dispatch(showSuccessToast('User removed successfully.'));

        // Refresh the team members list so the removed user disappears
        dispatch(getTeamMembersOperation({ page: 1 }));

        return { success: true, data: response };
    } catch (error) {
        dispatch(showErrorToast(
            'Failed to remove user',
            'Something went wrong. Please try again.',
        ));

        return { success: false, data: error };
    }
};
